import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { TYPE_META, PRIORITY_META, type Item } from "@/lib/types";
import { deadlineState, dueLabel, shortDate } from "@/lib/format";
import { Dot, ParentPill, ProgressBar, cardTitle } from "./atoms";
import { stripLinks, withLinks } from "@/lib/links";
import { SwipeRow } from "./SwipeRow";
import { todoistNesting } from "@/lib/hierarchy";
import { SubtaskList } from "./SubtaskList";

// A column is now an opaque {key,name,dot} triple rather than a hard-coded
// ItemStatus, so one board can group by phase OR by status (see handoff §2).
export interface Column {
  key: string;
  name: string;
  dot: string;
}

type SubCount = { done: number; total: number };

interface KanbanProps {
  items: Item[];
  columns: Column[];
  columnOf: (item: Item) => string; // which column an item belongs to
  onDropIn?: (item: Item, columnKey: string) => void; // drag-and-drop
  secondary?: (item: Item) => ReactNode; // chip for the *other* dimension
  subCounts?: Map<string, SubCount>; // progress per item (§7: children before checklist)
  parentOf?: (item: Item) => Item | undefined; // §7: the deliverable/milestone a task belongs to
  onComplete?: (item: Item) => void;           // §15.1 swipe left
  onDefer?: (item: Item) => void;              // §15.1 swipe right
  compare?: (a: Item, b: Item) => number;      // how cards order inside a column
  onReorder?: (item: Item, dir: -1 | 1) => void; // manual ordering, up/down
  onAddIn?: (columnKey: string) => void;       // "+ Add item" at the foot of a column
  /**
   * Drop with a position. When present this supersedes onDropIn, because it
   * carries the insertion index as well as the column — which is what makes
   * dragging a card up and down inside its own column mean something.
   */
  onDropAt?: (item: Item, columnKey: string, index: number) => void;
  onToggle?: (item: Item) => void;             // the completion circle on a card
  columnWidth?: number;
  onOpen?: (item: Item) => void;
  onMove?: (item: Item, dir: -1 | 1) => void; // arrows (mobile / no-drag)
  renderColumnHeader?: (col: Column, count: number) => ReactNode; // full header override (§3)
  trailing?: ReactNode; // e.g. the "+ Add phase" slot at the strip's end
  mobile?: boolean;
}

export function Kanban({
  items, columns, columnOf, onDropIn, secondary, subCounts, parentOf, onComplete, onDefer,
  compare, onReorder, onAddIn, onDropAt, onToggle, columnWidth = 288,
  onOpen, onMove, renderColumnHeader, trailing, mobile,
}: KanbanProps) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);
  // Where the card would land: the gap index inside overCol.
  const [overAt, setOverAt] = useState<number | null>(null);
  // Mobile: a chip row selects one column; its cards render as a vertical list.
  const [sel, setSel] = useState<string>(columns[0]?.key ?? "");

  // Grouping can change under us (Phase↔Status) — the old `sel` would hold a
  // stale key and the chip would silently jump to the first column. Reset it
  // only when the *set* of column keys changes, keeping a still-valid pick.
  const colSig = columns.map((c) => c.key).join("|");
  useEffect(() => {
    setSel((prev) => (columns.some((c) => c.key === prev) ? prev : columns[0]?.key ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colSig]);

  // Todoist subtasks ride inside their parent's card, as in Todoist — but only
  // when both land in the same column; otherwise the child keeps its own card.
  const nest = todoistNesting(items, (child, parent) => columnOf(child) === columnOf(parent));

  // Order inside a column is the caller's decision; with no comparator the
  // list keeps the order it arrived in, which is `position`. Nested subtasks
  // are not cards of their own, so they are not in the column's list.
  const inCol = (key: string) => {
    const list = items.filter((i) => columnOf(i) === key && !nest.isNested(i));
    return compare ? [...list].sort(compare) : list;
  };

  const dragEnabled = !!onDropIn || !!onDropAt;

  function handleDrop(key: string) {
    const item = items.find((i) => i.id === dragId);
    const at = overAt;
    setDragId(null);
    setOverCol(null);
    setOverAt(null);
    if (!item) return;
    if (onDropAt) {
      // The caller works out what actually changed; a same-column drop is a
      // reorder rather than a no-op.
      onDropAt(item, key, at ?? inCol(key).length);
      return;
    }
    if (columnOf(item) !== key) onDropIn?.(item, key);
  }

  /** Which gap the pointer is nearest, from the card it is over. */
  function gapFor(e: { clientY: number }, el: HTMLElement, index: number): number {
    const r = el.getBoundingClientRect();
    return e.clientY < r.top + r.height / 2 ? index : index + 1;
  }

  // ---- Mobile: chip row + single column list (handoff §6.2) ----------------
  if (mobile) {
    const active = columns.find((c) => c.key === sel) ?? columns[0];
    const activeItems = active ? inCol(active.key) : [];
    return (
      <div>
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 10, WebkitOverflowScrolling: "touch" }}>
          {columns.map((c) => {
            const on = c.key === (active?.key ?? "");
            return (
              <button
                key={c.key}
                onClick={() => setSel(c.key)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 12px", borderRadius: 999,
                  border: on ? "1px solid var(--accentLine)" : "1px solid var(--line)", cursor: "pointer",
                  background: on ? "var(--accentTint)" : "var(--surface)", color: on ? "var(--accent)" : "var(--ink2)",
                  fontSize: 12.5, fontWeight: on ? 500 : 400, whiteSpace: "nowrap", flex: "none",
                }}
              >
                <Dot color={c.dot} size={7} />
                {c.name}
                <span className="mono" style={{ fontSize: 10.5, color: on ? "var(--accent)" : "var(--muted2)" }}>{inCol(c.key).length}</span>
              </button>
            );
          })}
          {trailing}
        </div>
        {active && renderColumnHeader && <div style={{ margin: "2px 0 10px" }}>{renderColumnHeader(active, activeItems.length)}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--card-gap)", marginTop: 4, minHeight: 24 }}>
          {activeItems.map((it) => (
            <SwipeRow
                key={it.id}
                onComplete={onComplete ? () => onComplete(it) : undefined}
                onDefer={onDefer ? () => onDefer(it) : undefined}
                completeLabel={it.status === "done" ? "Reopen" : "Complete"}
              >
                <Card item={it} secondary={secondary} sub={subCounts?.get(it.id)} parent={parentOf?.(it)} childrenOf={nest.childrenOf} onOpen={onOpen} onMove={onMove} onToggle={onToggle} draggable={false} />
              </SwipeRow>
          ))}
          {activeItems.length === 0 && <div style={{ fontSize: 12.5, color: "var(--muted2)", padding: "10px 2px" }}>Nothing here yet.</div>}
        </div>
      </div>
    );
  }

  // ---- Desktop: horizontal columns with drag -------------------------------
  return (
    <div style={{ display: "flex", gap: 16, overflowX: "auto", paddingBottom: 8, alignItems: "flex-start" }}>
      {columns.map((c) => {
        const col = inCol(c.key);
        const isOver = overCol === c.key;
        return (
          <div
            key={c.key}
            // §bug1: no onDragLeave — dragging over a child card fires dragleave
            // on the column and strobes the highlight. Set overCol on dragOver
            // only (guarded so we don't setState every mousemove), and clear it
            // on drop / dragEnd. That keeps the drop state rock-steady.
            onDragOver={dragEnabled ? (e) => {
              e.preventDefault();
              if (overCol !== c.key) { setOverCol(c.key); setOverAt(null); }
            } : undefined}
            onDrop={dragEnabled ? (e) => { e.preventDefault(); handleDrop(c.key); } : undefined}
            style={{
              width: columnWidth, flex: "none", borderRadius: 14, padding: 6,
              // A column is invisible until you drag over it — cards float
              // directly on the pane; the drop target is the only fill.
              background: isOver ? "var(--accentTint)" : "transparent",
              outline: isOver ? "2px dashed var(--accentLine)" : "none",
              outlineOffset: -2,
              transition: "background 0.1s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, minHeight: 24 }}>
              {renderColumnHeader ? (
                renderColumnHeader(c, col.length)
              ) : (
                <>
                  <Dot color={c.dot} size={8} />
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span>
                  <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted2)" }}>{col.length}</span>
                </>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--card-gap)", minHeight: 24 }}>
              {col.map((it, i) => (
                <div
                  key={it.id}
                  onDragOver={onDropAt ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const gap = gapFor(e, e.currentTarget as HTMLElement, i);
                    if (overCol !== c.key) setOverCol(c.key);
                    if (overAt !== gap) setOverAt(gap);
                  } : undefined}
                  style={{
                    // The line sits in the gap the card would drop into.
                    borderTop: onDropAt && overCol === c.key && overAt === i ? "2px solid var(--accent)" : "2px solid transparent",
                    borderBottom: onDropAt && overCol === c.key && overAt === i + 1 && i === col.length - 1 ? "2px solid var(--accent)" : "2px solid transparent",
                  }}
                >
                <Card
                  item={it}
                  secondary={secondary}
                  sub={subCounts?.get(it.id)}
                  parent={parentOf?.(it)}
                  childrenOf={nest.childrenOf}
                  onOpen={onOpen}
                  // Drag and drop covers both moves on desktop. The arrows
                  // were hidden until hover but still reserved a row on every
                  // card, so they stay only where dragging is unavailable.
                  onMove={dragEnabled ? undefined : onMove}
                  onReorder={dragEnabled ? undefined : onReorder}
                  onToggle={onToggle}
                  draggable={dragEnabled}
                  onDragStart={() => setDragId(it.id)}
                  onDragEnd={() => { setDragId(null); setOverCol(null); setOverAt(null); }}
                  dragging={dragId === it.id}
                />
                </div>
              ))}
              {col.length === 0 && !onAddIn && <div style={{ fontSize: 12, color: "var(--muted2)", padding: "6px 2px" }}>—</div>}
              {onAddIn && (
                <button
                  onClick={() => onAddIn(c.key)}
                  style={{
                    width: "100%", padding: "8px 10px", borderRadius: 12,
                    border: "1px dashed var(--line2)", background: "none",
                    color: "var(--muted)", fontSize: 12.5, cursor: "pointer", textAlign: "left",
                  }}
                >
                  + Add item
                </button>
              )}
            </div>
          </div>
        );
      })}
      {trailing}
    </div>
  );
}

function Card({ item, secondary, sub, parent, childrenOf, onOpen, onMove, onReorder, onToggle, draggable, onDragStart, onDragEnd, dragging }: {
  item: Item; secondary?: (i: Item) => ReactNode; sub?: SubCount; parent?: Item;
  childrenOf?: (i: Item) => Item[];
  onOpen?: (i: Item) => void; onMove?: (i: Item, dir: -1 | 1) => void;
  onReorder?: (i: Item, dir: -1 | 1) => void; onToggle?: (i: Item) => void;
  draggable?: boolean; onDragStart?: () => void; onDragEnd?: () => void; dragging?: boolean;
}) {
  // The pill is the SCHEDULED day (Todoist's date) and the flag on line 2 is
  // the DEADLINE. The pill used to read `item.due`, so a card for a task
  // scheduled for today with no deadline showed no date at all, and one with
  // only a deadline showed it as though it were the day to do the work.
  const due = dueLabel(item.start, "", item.status);
  const deadline = deadlineState(item);
  const deadlineText = item.due ? shortDate(item.due) : "";
  const chip = secondary?.(item);
  const done = item.status === "done";
  const subtasks = childrenOf?.(item) ?? [];
  // Urgent and high get a coloured edge so they stand out of a full column
  // without having to read every card.
  const edge = item.priority === "urgent" ? "var(--bad)"
    : item.priority === "high" ? "var(--warn)"
    : null;
  return (
    <div
      className="board-card"
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onOpen?.(item)}
      style={{
        display: "flex", flexDirection: "column", gap: 7,
        background: "var(--surface)", borderRadius: 12, padding: "var(--card-py)",
        border: "1px solid var(--line)",
        boxShadow: "var(--shadowCard)",
        // Priority rides on its own channel so the hover border can do its job.
        backgroundImage: edge ? `linear-gradient(to right, ${edge} 0 3px, transparent 3px)` : undefined,
        // The gutter is reserved on every card, not just the ones that use it,
        // so titles line up down a column of mixed priorities.
        paddingLeft: "calc(var(--card-py) + 4px)",
        cursor: draggable ? "grab" : onOpen ? "pointer" : "default", opacity: dragging ? 0.4 : 1,
      }}
    >
      {/* Line 1: completion, type glyph, title, due. The type LABEL is gone —
          the glyph carries it, with a legend above the board. */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        {onToggle && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(item); }}
            aria-label={done ? "Mark incomplete" : "Mark complete"}
            title={done ? "Mark incomplete" : "Mark complete"}
            style={{
              width: 18, height: 18, flex: "none", marginTop: 1, borderRadius: "50%",
              border: done ? "none" : "1.5px solid var(--line2)",
              background: done ? "var(--good)" : "transparent",
              color: "var(--onAccent)", cursor: "pointer", fontSize: 11,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
            }}
          >
            {done ? "✓" : ""}
          </button>
        )}
        <span
          title={TYPE_META[item.type].label}
          style={{ color: TYPE_META[item.type].color, fontSize: 11, flex: "none", marginTop: 2 }}
        >{TYPE_META[item.type].glyph}</span>
        <span
          title={stripLinks(item.title)}
          style={{
            ...cardTitle, flex: 1, minWidth: 0,
            textDecoration: done ? "line-through" : "none",
            color: done ? "var(--muted)" : undefined,
          }}
        >{withLinks(item.title, item.id)}</span>
        {due.text && (
          <span
            style={{
              flex: "none",
              fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 999,
              color: due.overdue ? "var(--bad)" : due.soon ? "var(--warn)" : "var(--ink3)",
              background: due.overdue ? "var(--badTint)" : due.soon ? "var(--warnTint)" : "var(--tint)",
            }}
          >
            {due.text}
          </span>
        )}
      </div>

      {/* Line 2 only exists when it has something to say. The priority LABEL is
          gone (the 3px edge carries it) and so is the subtask progress bar (the
          count beside it said the same thing). */}
      {(chip || deadline || (sub && sub.total > 0) || parent) && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {chip}
          {deadline && (
            <span
              className="mono"
              title={`Deadline ${deadlineText}`}
              style={{
                fontSize: 10.5,
                color: deadline === "overdue" ? "var(--bad)" : deadline === "later" ? "var(--muted2)" : "var(--warn)",
              }}
            >⚑ {deadlineText}</span>
          )}
          {sub && sub.total > 0 && (
            <span className="mono" style={{ fontSize: 11, color: "var(--ink3)" }}>{sub.done}/{sub.total}</span>
          )}
          {parent && <ParentPill parent={parent} />}
        </div>
      )}

      {subtasks.length > 0 && (
        <SubtaskList items={subtasks} childrenOf={childrenOf!} onOpen={onOpen} onToggle={onToggle} style={{ marginTop: 1 }} />
      )}

      {(onReorder || onMove) && (
        <div className="board-card-actions" style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          {onReorder && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onReorder(item, -1); }} style={moveBtn} aria-label="Move up" title="Move up">↑</button>
              <button onClick={(e) => { e.stopPropagation(); onReorder(item, 1); }} style={moveBtn} aria-label="Move down" title="Move down">↓</button>
            </>
          )}
          {onMove && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onMove(item, -1); }} style={moveBtn} aria-label="Move back">←</button>
              <button onClick={(e) => { e.stopPropagation(); onMove(item, 1); }} style={moveBtn} aria-label="Move forward">→</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// Chip helpers used by callers for the "other dimension" (§2). Both are solid
// tonal pills so switching Phase↔Status doesn't change the card's vocabulary.
export function PhaseChip({ name, color }: { name?: string; color?: string }) {
  if (!name) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 500,
      padding: "3px 9px", borderRadius: 999, background: "var(--tint)", color: "var(--ink2)",
    }}>
      {color && <Dot color={color} size={7} />}
      {name}
    </span>
  );
}

export function ProjectChip({ name, color }: { name?: string; color?: string }) {
  if (!name) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 500,
      padding: "3px 9px", borderRadius: 999, background: "var(--tint)", color: "var(--ink2)",
    }}>
      {color && <Dot color={color} size={7} />}
      {name}
    </span>
  );
}

export const moveBtn: CSSProperties = {
  width: 24, height: 24, borderRadius: 999, border: "1px solid var(--tint2)", background: "var(--surface)",
  color: "var(--ink3)", cursor: "pointer", fontSize: 11, lineHeight: 1, padding: 0,
};
